/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../../../../logger';
import { generateUUID, getGlobalPrebidObj } from '../../../../util';
import { SiteConfig } from '../../../config';
import { BaseVideoPlacement, Quartile } from '../index';

import { CampaignType, type CampaignData, type RenderedAdData } from '../../../../types/index';
import type { PrebidAdUnit, PrebidBidResponse } from '../../../../types/prebid';
import type {
  DailyMotionPlayer,
  DailyMotionPlayerState,
  DailyMotionWindow,
} from '../../../../types/vendor/dailymotion';

const logger = Logger.getInstance();

// The Dailymotion event payload's adId is the final inline VAST <Ad id> (see
// docs/DAILYMOTION.md) — matching it against the <Ad id> parsed from each
// Prebid video bid's vastXml is how Prebid-won ads are recognised.
const VAST_AD_ID_REGEX = /<Ad[^>]*\sid="([^"]*)"/;

// Fraction of an ad that must have been watched (per the last AD_TIMECHANGE)
// for it to count as completed when finalised. AD_END always arrives nulled and
// there is no AD_SKIP event, so this progress gate is the only way to tell a
// natural completion from a user skip. Set at 0.90 because AD_TIMECHANGE ticks
// land ~1-2s short of the end (observed last tick ~91% on a fully-played ad),
// while a skip happens far earlier (skip offset is typically a few seconds in).
const AD_COMPLETION_THRESHOLD = 0.9;

class DailyMotionVideoPlacement extends BaseVideoPlacement {
  private dailyMotionWindow: DailyMotionWindow;
  private playerPromise: Promise<DailyMotionPlayer> | null = null;
  private playerRootNode: HTMLElement | null = null;
  private rootNodeRequested: boolean = false;
  private listenersSetup: boolean = false;
  private prebidBidsByVastAdId: Map<string, PrebidBidResponse> = new Map();
  // Identity of the ad currently being tracked: the adId when present, else
  // the creative id (GAM direct-sold ads surface adId null — see
  // docs/DAILYMOTION.md). AD_START with a different key marks an ad boundary
  // (the prior ad ended, a new one begins) — AD_END is unreliable in
  // pods/auto-next, so AD_START is the authoritative per-ad signal. The same
  // creative playing twice back-to-back with null adIds is indistinguishable
  // from a duplicate AD_START and is deduped (undercount over overcount).
  private currentAdBoundaryKey: string | null = null;
  // Latest playback progress of the current ad (from AD_TIMECHANGE), used to
  // decide whether to synthesise a completion for a prior ad whose AD_END never
  // arrived before the next ad started.
  private lastAdProgress: number = 0;

  constructor(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig,
  ) {
    super(adUnit, adUnitTransformer, campaignData, siteConfig);
    this.dailyMotionWindow = window as DailyMotionWindow;
  }

  private getPlayer(): Promise<DailyMotionPlayer> | null {
    const sdk = this.dailyMotionWindow.dailymotion;
    if (!sdk) {
      return null;
    }
    this.playerPromise ??= sdk.getPlayer();
    return this.playerPromise;
  }

  /**
   * Unlike GPT/AST/Digiteka, the slot lookup itself is asynchronous: even once
   * `window.dailymotion` exists, the housing element is only reachable via the
   * async `getPlayer()` (then the synchronous `player.getRootNode()`).
   *
   * The base class only runs its slot-found completion sequence (clear the
   * detection interval, capture dimensions, attach listeners) when `this.slot`
   * transitions inside a `determineSlot()` call — so the promise callback must
   * NOT assign `this.slot` directly. Instead it stages the element in
   * `playerRootNode` and re-enters `determineSlot()`, which calls back into
   * this method to perform the assignment synchronously. `setSlot()` is the
   * only writer of `this.slot`.
   */
  protected setSlot(): void {
    if (this.slot !== null) {
      return;
    }

    if (this.playerRootNode) {
      this.slot = this.playerRootNode;
      return;
    }

    if (this.rootNodeRequested) {
      return; // resolution pending; base class slot detection will re-poll
    }

    const playerPromise = this.getPlayer();
    if (!playerPromise) {
      return; // SDK not on the page yet; base class slot detection will re-poll
    }

    this.rootNodeRequested = true;
    playerPromise
      .then((player) => {
        this.playerRootNode = player.getRootNode();
        if (this.playerRootNode) {
          this.determineSlot();
        } else {
          logger.warn('Dailymotion player has no root node');
        }
      })
      .catch((error) => {
        this.rootNodeRequested = false;
        this.playerPromise = null;
        logger.warn(`Failed to obtain Dailymotion player: ${error}`);
      });
  }

  protected setSupportedSizes(): void {}

  /**
   * On scroll Dailymotion docks the `.dailymotion-player-wrapper` child as a
   * fixed mini-player while the root element stays in document flow (verified
   * live) — so viewability must be measured against the wrapper, which tracks
   * the visible player in both states. Docked playback counts as in view.
   *
   * The class name is Dailymotion's to rename, so fall back to the root's
   * first element child (the wrapper is the root's only child — verified
   * live), then to the root itself.
   */
  protected getViewabilityElement(): HTMLElement | null {
    if (!this.slot) {
      return null;
    }
    const slotElement = this.slot as HTMLElement;
    return (
      (slotElement.querySelector('.dailymotion-player-wrapper') as HTMLElement | null) ??
      (slotElement.firstElementChild as HTMLElement | null) ??
      slotElement
    );
  }

  protected addListeners(): void {
    const playerPromise = this.getPlayer();
    if (!playerPromise) {
      logger.warn('Dailymotion SDK is not available on the page');
      return;
    }

    playerPromise
      .then((player) => this.setupPlayerListeners(player))
      .catch((error) => logger.warn(`Failed to obtain Dailymotion player: ${error}`));
  }

  protected addAdInteractionListener(): void {
    // Click listener is added in setupPlayerListeners
  }

  private setupPlayerListeners(player: DailyMotionPlayer): void {
    if (this.listenersSetup) {
      return;
    }

    const sdk = this.dailyMotionWindow.dailymotion;
    if (!sdk) {
      return;
    }

    this.listenersSetup = true;

    const events = sdk.events;
    player.on(events.AD_READYTOFETCH, () => this.refreshPrebidVideoBids());
    player.on(events.AD_START, (eventData) => this.adStartHandler(eventData));
    player.on(events.AD_PAUSE, () => this.pauseViewabilityTracking());
    player.on(events.AD_PLAY, () => this.resumeViewabilityTracking());
    player.on(events.AD_TIMECHANGE, (eventData) => this.adTimeChangeHandler(eventData));
    player.on(events.AD_END, (eventData) => this.adEndHandler(eventData));
    player.on(events.AD_CLICK, (eventData) => this.adClickHandler(eventData));
  }

  private refreshPrebidVideoBids(): void {
    const pbjs = getGlobalPrebidObj();
    if (!pbjs?.getBidResponsesForAdUnitCode) {
      return;
    }

    this.prebidBidsByVastAdId.clear();
    // Prebid tracks responses by the original (untransformed) ad unit code
    const bids = pbjs.getBidResponsesForAdUnitCode(this.adUnit.code)?.bids ?? [];
    for (const bid of bids) {
      if (bid.mediaType !== 'video') {
        continue;
      }
      if (!bid.vastXml) {
        logger.debug(`Skipping ${bid.bidder} video bid without inline VAST for ${this.adUnitCode}`);
        continue;
      }
      const vastAdId = bid.vastXml.match(VAST_AD_ID_REGEX)?.[1];
      if (vastAdId) {
        this.prebidBidsByVastAdId.set(vastAdId, bid);
      }
    }
  }

  private adStartHandler(eventData: DailyMotionPlayerState): void {
    // AD_START is the authoritative ad boundary. AD_END is unreliable in
    // pods/auto-next (late, nulled, or missing), so a new ad identity — not
    // the prior ad's AD_END — is what ends the previous ad and begins the
    // next. The crid: prefix keeps a numeric adId from colliding with an
    // equal numeric creative id across consecutive ads.
    const boundaryKey =
      eventData.adId ?? (eventData.adCreativeId ? `crid:${eventData.adCreativeId}` : null);
    if (boundaryKey !== null && boundaryKey === this.currentAdBoundaryKey) {
      // Duplicate AD_START for the same ad; ignore.
      return;
    }

    // Boundary: finalise the ad that was in progress (if any) before starting
    // the new one. adStarted() below claims/mints a distinct opportunity.
    this.finalisePreviousAd();
    this.currentAdBoundaryKey = boundaryKey;
    this.lastAdProgress = 0;

    this.adStarted();

    const currentOpportunity = this.currentOpportunity;
    if (currentOpportunity && this.completedOpportunityIds.has(currentOpportunity.id)) {
      return;
    }

    if (this.prebidBidsByVastAdId.size === 0) {
      // Covers a missed AD_READYTOFETCH (e.g. listeners attached mid-break)
      this.refreshPrebidVideoBids();
    }
    const matchedBid = eventData.adId ? this.prebidBidsByVastAdId.get(eventData.adId) : undefined;
    if (matchedBid) {
      this.handlePrebidWonAd(matchedBid, eventData);
      return;
    }

    // Genuine GAM direct-sold ads surface adId null with the GAM creative id
    // in adCreativeId (verified via GAM preview capture — see
    // docs/DAILYMOTION.md), so a numeric creative id is treated as direct
    // sold: matched creatives record against their configured ad, the rest
    // fall through to the direct learning path inside handleRenderedAdData.
    if (!eventData.adId) {
      const creativeId = eventData.adCreativeId;
      if (creativeId && /^\d+$/.test(creativeId)) {
        this.handleRenderedAdData(this.buildDirectRenderedAdData(eventData, undefined, creativeId));
      } else {
        logger.debug(
          `Ignoring Dailymotion ad without line or numeric creative id (${creativeId}) for ${this.adUnitCode}`,
        );
      }
      return;
    }

    // Only Google-stack-served ads carry numeric VAST line ids; non-numeric
    // ids (e.g. DSP hashes) indicate Ad Exchange programmatic buys, which are
    // not monitored — see docs/DAILYMOTION.md.
    if (!/^\d+$/.test(eventData.adId)) {
      logger.debug(
        `Ignoring Dailymotion ad with non-numeric line id (${eventData.adId}) for ${this.adUnitCode}`,
      );
      return;
    }

    this.handleRenderedAdData(
      this.buildDirectRenderedAdData(eventData, eventData.adId, eventData.adCreativeId ?? undefined),
    );
  }

  private buildDirectRenderedAdData(
    eventData: DailyMotionPlayerState,
    lineItemId: string | undefined,
    creativeId: string | undefined,
  ): RenderedAdData {
    const adDuration = eventData.adDuration ?? 0;
    return {
      lineItemId,
      creativeId,
      durationSeconds: adDuration > 0 ? Math.round(adDuration) : undefined,
      isPrebidAd: false,
      isProgrammaticBuy: false,
      responseIdentifier: generateUUID(),
      size: {
        width: eventData.adWidth || this.playerWidth,
        height: eventData.adHeight || this.playerHeight,
      },
    };
  }

  /**
   * Prebid-won ads cannot reuse the deferred display flow (handleRenderedAdData
   * with isPrebidAd: true waits for pbjs 'adRenderSucceeded', which never fires
   * for ads rendered by GAM/IMA inside the cross-origin player iframe), so the
   * impression is dispatched inline, mirroring addPrebidAdRenderedListener.
   */
  private handlePrebidWonAd(bid: PrebidBidResponse, eventData: DailyMotionPlayerState): void {
    const advertiserDomains = this.cleanAdvertiserDomains(bid.meta?.advertiserDomains ?? []);
    if (advertiserDomains.length === 0) {
      logger.debug(
        `Ignoring Prebid-won Dailymotion ad for ${this.adUnitCode}: no valid advertiser domains`,
      );
      return;
    }

    logger.debug(
      `Dailymotion ad matched Prebid ${bid.bidder} bid for ${this.adUnitCode} via VAST ad id`,
    );

    const adDuration = eventData.adDuration ?? 0;
    const bidCreativeId =
      bid.creativeId !== undefined && bid.creativeId !== null ? String(bid.creativeId) : '';
    const nodalsDealId = bid.dealId && this.hasDealActivated(bid.dealId) ? bid.dealId : undefined;

    this.renderedAdData = {
      advertiserDomains,
      creativeId: bidCreativeId || eventData.adCreativeId || undefined,
      durationSeconds: adDuration > 0 ? Math.round(adDuration) : undefined,
      nodalsDealId,
      isPrebidAd: true,
      isProgrammaticBuy: true,
      responseIdentifier: generateUUID(),
      size: {
        width: eventData.adWidth || this.playerWidth,
        height: eventData.adHeight || this.playerHeight,
      },
    };

    const deliveryId = nodalsDealId
      ? this.getDealCampaignDeliveryId(nodalsDealId)
      : this.getLearningCampaignDeliveryId(CampaignType.LEARNING_PROGRAMMATIC);
    if (deliveryId) {
      this.dispatchProgrammaticServedAdImpression(deliveryId);
    }
  }

  private adTimeChangeHandler(eventData: DailyMotionPlayerState): void {
    const adDuration = eventData.adDuration ?? 0;
    if (!this.adCurrentlyPlaying || adDuration <= 0) {
      return;
    }

    const adTime = eventData.adTime ?? 0;
    const progress = adTime / adDuration;
    this.lastAdProgress = progress;
    if (progress >= 0.25) {
      this.dispatchAdQuartileEvent(Quartile.FIRST);
    }
    if (progress >= 0.5) {
      this.dispatchAdQuartileEvent(Quartile.SECOND);
    }
    if (progress >= 0.75) {
      this.dispatchAdQuartileEvent(Quartile.THIRD);
    }
  }

  private adEndHandler(_eventData: DailyMotionPlayerState): void {
    // Completion path for the last ad in a break (no following AD_START boundary
    // to finalise it). Only acts while an ad is in progress so a late/stray
    // AD_END from a prior pod ad cannot clobber a newer ad that already started.
    if (!this.adCurrentlyPlaying) {
      return;
    }
    this.finaliseCurrentAd();
  }

  /**
   * Finalise the ad that was in progress at an AD_START boundary (the prior ad
   * ended because a new one began — AD_END is unreliable in pods).
   */
  private finalisePreviousAd(): void {
    if (!this.adCurrentlyPlaying) {
      return;
    }
    this.finaliseCurrentAd();
  }

  /**
   * Single completion rule shared by both finalise paths (AD_END and the
   * AD_START boundary). The DM AD_END payload always arrives nulled (adTime 0 /
   * adDuration 0 — verified live), and there is no AD_SKIP event, so a skip and
   * a true completion are indistinguishable from AD_END itself. The only
   * reliable signal is the last AD_TIMECHANGE progress: synthesise the fourth
   * quartile only if the ad was watched to >= AD_COMPLETION_THRESHOLD. A
   * user-skipped ad (ended early) therefore keeps the quartiles it actually
   * reached but is never counted as completed. dispatchAdQuartileEvent dedupes
   * per opportunity, so a natural 100% AD_TIMECHANGE that already fired Q4 makes
   * this a no-op.
   */
  private finaliseCurrentAd(): void {
    if (this.lastAdProgress >= AD_COMPLETION_THRESHOLD) {
      this.dispatchAdQuartileEvent(Quartile.FOURTH);
    }
    this.adCurrentlyPlaying = false;
  }

  private adClickHandler(_eventData: DailyMotionPlayerState): void {
    if (!this.adCurrentlyPlaying) {
      return;
    }
    this.dispatchAdInteraction();
  }
}

export { DailyMotionVideoPlacement };
